import { useEffect, useState } from "react";
import api, { apiErrorMessage, formatPLN } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import CsvImportModal from "@/components/CsvImportModal";
import { toast } from "sonner";
import { Plus, Save, X, Upload } from "lucide-react";

const EMPTY = { sku: "", name: "", description: "", category: "Biuro", unit: "szt.", price_net: 0, vat: 23, active: true };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => api.get("/products", { params: { active_only: false } }).then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", { ...form, price_net: Number(form.price_net), vat: Number(form.vat) });
      toast.success("Dodano produkt");
      setAdding(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const save = async (id) => {
    try {
      await api.patch(`/products/${id}`, { ...edit, price_net: Number(edit.price_net), vat: Number(edit.vat) });
      toast.success("Zapisano");
      setEditingId(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Dezaktywować produkt? Produkt zniknie z katalogu, ale historia zamówień pozostanie.")) return;
    try {
      await api.delete(`/products/${id}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const hardDelete = async (p) => {
    if (!window.confirm(`USUNĄĆ TRWALE produkt "${p.name}" (SKU: ${p.sku})?\n\nTo jest nieodwracalne. Jeśli produkt występował w jakichkolwiek zamówieniach, system zablokuje usunięcie i będziesz musiał użyć dezaktywacji.`)) return;
    try {
      await api.delete(`/products/${p.id}?hard=true`);
      toast.success("Produkt usunięty trwale");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div>
      <PageHeader testId="admin-products" title="Produkty" subtitle={`${products.length} pozycji w katalogu`}
        actions={
          <>
            <button onClick={() => setImportOpen(true)} data-testid="import-products-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 hover:border-[#FF6B1A] hover:text-[#FF6B1A] text-sm font-semibold transition-colors">
              <Upload className="w-4 h-4" /> Importuj CSV
            </button>
            <button onClick={() => setAdding(true)} data-testid="add-product-cta"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold">
              <Plus className="w-4 h-4" /> Dodaj produkt
            </button>
          </>
        } />

      {adding && (
        <form onSubmit={create} className="ol-card p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Nowy produkt</h3>
            <button type="button" onClick={() => setAdding(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <input required placeholder="SKU" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="prod-sku" className="px-3 py-2 rounded-md border border-slate-300 text-sm" />
            <input required placeholder="Nazwa" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" className="col-span-2 px-3 py-2 rounded-md border border-slate-300 text-sm" />
            <input required placeholder="Kategoria" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-3 py-2 rounded-md border border-slate-300 text-sm" />
            <input required placeholder="Jedn." value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="px-3 py-2 rounded-md border border-slate-300 text-sm" />
            <input required type="number" step="0.01" placeholder="Cena netto" value={form.price_net} onChange={(e) => setForm({ ...form, price_net: e.target.value })} data-testid="prod-price" className="px-3 py-2 rounded-md border border-slate-300 text-sm" />
          </div>
          <div className="mt-3">
            <input placeholder="Krótki opis (opcjonalnie) — np. opakowanie, gramatura, główne cechy" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="prod-description"
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
          </div>
          <div className="flex justify-end mt-4">
            <button type="submit" className="px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold" data-testid="create-product-submit">Utwórz</button>
          </div>
        </form>
      )}

      <div className="ol-card overflow-hidden">
        <table className="ol-table" data-testid="products-table">
          <thead>
            <tr><th>SKU</th><th>Nazwa</th><th>Kategoria</th><th>Jedn.</th><th className="text-right">Cena netto</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {products.map((p) => editingId === p.id ? (
              <tr key={p.id}>
                <td><input value={edit.sku} onChange={(e) => setEdit({ ...edit, sku: e.target.value })} className="w-24 px-2 py-1 rounded border border-slate-300 text-xs" /></td>
                <td><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="w-full px-2 py-1 rounded border border-slate-300 text-sm" /></td>
                <td><input value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className="w-28 px-2 py-1 rounded border border-slate-300 text-xs" /></td>
                <td><input value={edit.unit} onChange={(e) => setEdit({ ...edit, unit: e.target.value })} className="w-20 px-2 py-1 rounded border border-slate-300 text-xs" /></td>
                <td className="text-right"><input type="number" step="0.01" value={edit.price_net} onChange={(e) => setEdit({ ...edit, price_net: e.target.value })} className="w-24 text-right px-2 py-1 rounded border border-slate-300 text-sm" /></td>
                <td>{edit.active ? "Aktywny" : "Nieaktywny"}</td>
                <td className="flex gap-1">
                  <button onClick={() => save(p.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#FF6B1A] text-white text-xs font-semibold"><Save className="w-3 h-3" /></button>
                  <button onClick={() => setEditingId(null)} className="px-2 py-1 rounded border border-slate-300 text-xs">×</button>
                </td>
              </tr>
            ) : (
              <tr key={p.id}>
                <td className="font-mono text-xs">{p.sku}</td>
                <td>
                  <p className="font-medium">{p.name}</p>
                  {p.description && <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>}
                </td>
                <td><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{p.category}</span></td>
                <td className="text-slate-600">{p.unit}</td>
                <td className="text-right font-semibold">{formatPLN(p.price_net)}</td>
                <td>{p.active ? <span className="ol-badge bg-emerald-50 text-emerald-700 border border-emerald-200">Aktywny</span> : <span className="ol-badge bg-slate-100 text-slate-500 border border-slate-200">Nieaktywny</span>}</td>
                <td className="space-x-2">
                  <button onClick={() => { setEditingId(p.id); setEdit({ ...p }); }} data-testid={`edit-product-${p.sku}`} className="text-xs font-semibold text-slate-600 hover:text-[#FF6B1A]">Edytuj</button>
                  {p.active && <button onClick={() => deactivate(p.id)} className="text-xs font-semibold text-amber-700 hover:text-amber-800">Dezaktywuj</button>}
                  <button onClick={() => hardDelete(p)} data-testid={`hard-delete-${p.sku}`} className="text-xs font-semibold text-red-600 hover:text-red-700">Usuń trwale</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {importOpen && (
        <CsvImportModal
          endpoint="/import/products"
          title="Importuj produkty z CSV"
          headers={["sku", "name", "description", "category", "unit", "price_net", "vat", "active"]}
          sample={[
            ["BIU-031", "Klips biurowy 50mm (op. 12 szt.)", "12 szt., metalowy, do 250 kartek", "Biuro", "opak.", "8.40", "23", "true"],
            ["BHP-009", "Rękawice nitrylowe (op. 100 szt.)", "100 szt., niebieskie, bez pudru, rozmiar L", "BHP", "opak.", "32.00", "23", "true"],
          ]}
          templateName="szablon_produkty.csv"
          onClose={() => setImportOpen(false)}
          onDone={load}
        />
      )}
    </div>
  );
}
